// Modules
import moment from "moment";

// Interfaces
import { GetReservationsResult } from 'Interfaces/GetReservations';
import { WaitlistManagementType } from "Interfaces/WaitlistManagementInterfaces";

enum SortType {
  WAITLISTID = "waitlistid",
  TRANSACTION_DATE = "transaction_date",
  };

const SORT_FIELDS = {
  [SortType.WAITLISTID]: "waitlistid",
  [SortType.TRANSACTION_DATE]: "lastUpdateDate",
}

const RESERVATION_DETAILS = "reservationDetails"
const WAITLISTID = "waitlistId";

enum SortDirection {
  ASC = 'ascending',
  DESC = 'descending',
}

var resType: WaitlistManagementType;

/**
 * Evaluate a sort for two waitlist based on id
 * @param waitListIdA 
 * @param waitListIdB 
 * @param sortDirection 
 * @returns 
 */
function evalWaitlistIdSort(waitListIdA: number, waitListIdB: number, sortDirection: SortDirection) {
  let idCompare = waitListIdA - waitListIdB;
  
  if(sortDirection === SortDirection.DESC) {
    idCompare = waitListIdB - waitListIdA;
  }

  if(idCompare > 0) {
    return 1;
  } else if(idCompare < 0) {
    return -1;
  }

  return 0;
}

/**
 * Evaluate a sort for two reservations based on destination
 */
 function evalDestinationSort(destinationA: string, destinationB: string, sortDirection: SortDirection) {
  //remove special characters that may affect sorting (i.e. different apostrophes)
  let a_stripped = destinationA.replace(/[^a-zA-Z0-9\s]/g, "");
  let b_stripped = destinationB.replace(/[^a-zA-Z0-9\s]/g, "");
  
  let letterCompare = (a_stripped).localeCompare(b_stripped);
  if (sortDirection === SortDirection.DESC)
    letterCompare = (b_stripped).localeCompare(a_stripped);

  // Sort by letters in Usage Type was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Usage Types are identical
  return 0;
}

/**
 * Evaluate a sort for two reservations based on date
 */
function evalDateSort(dateA: string, dateB: string, sortDirection: SortDirection) {
  const firstDate = moment(dateA);
  const secondDate = moment(dateB);

  let dateCompare = firstDate.valueOf() - secondDate.valueOf();
  if (sortDirection === SortDirection.DESC)
    dateCompare = secondDate.valueOf() - firstDate.valueOf();

  // Date sort was successful
  if (dateCompare > 0) {
    return 1;
  } else if (dateCompare < 0){
    return -1;
  }

  // Dates are identical
  return 0;
}

/**
 * Evaluate a sort for two reservations based on transaction date
 */
 function evalTransactionDateSort(transactionDateA: string, transactionDateB: string, sortDirection: SortDirection) {
  // const firstDateTime = transactionDateA.split('T')[1].split(':');
  // const secondDateTime = transactionDateB.split('T')[1].split(':');

  // const firstDate = moment(transactionDateA.split('T')[0]).set('hour', parseInt(firstDateTime[0])).set('minute', parseInt(firstDateTime[1])).set('second', parseInt(firstDateTime[2]));
  // const secondDate = moment(transactionDateB.split('T')[0]).set('hour', parseInt(secondDateTime[0])).set('minute', parseInt(secondDateTime[1])).set('second', parseInt(secondDateTime[2]));

  const firstDate = moment(transactionDateA);
  const secondDate = moment(transactionDateB);


  let dateCompare = firstDate.valueOf() - secondDate.valueOf();
  if (sortDirection === SortDirection.DESC)
    dateCompare = secondDate.valueOf() - firstDate.valueOf();

  // Date sort was successful
  if (dateCompare > 0) {
    return 1;
  } else if (dateCompare < 0){
    return -1;
  }

  // Dates are identical
  return 0;
}

/**
 * Evaluate a sort for two reservations based on reservation type
 */
 function evalReservationTypeSort(reservationTypeA: string, reservationTypeB: string, sortDirection: SortDirection) {
  let letterCompare = (reservationTypeA).localeCompare(reservationTypeB);
  if (sortDirection === SortDirection.DESC)
    letterCompare = (reservationTypeB).localeCompare(reservationTypeA);

  // Sort by letters in Usage Type was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Usage Types are identical
  return 0;
}

/**
 * Sort function based on waitlist id
 * The sort priorities are the following:
 * 1. waitlistid
 * @param a 
 * @param b 
 * @param sortDirection 
 */
function waitListIdSortFunc(a: any, b: any, sortDirection: SortDirection) {
  const sortResult = evalWaitlistIdSort(
    a[WAITLISTID],
    b[WAITLISTID],
    sortDirection
  );

  if(sortResult > 0) {
    return 1;
  } else if(sortResult < 0) {
    return -1;
  }

  return 0;
}

 function transactionDateSortFunc(a: any, b:any, sortDirection: SortDirection) {
  // Try to sort by Transaction Date
  const sortResult = evalTransactionDateSort(
    a[SORT_FIELDS[SortType.TRANSACTION_DATE]],
    b[SORT_FIELDS[SortType.TRANSACTION_DATE]],
    sortDirection
  );

  // Transaction Date sort was successful
  if (sortResult > 0) {
    return 1;
  } else if (sortResult < 0) {
    return -1;
  }

  // Transaction Dates are identical
  return 0;
}

/**
 * sort Reservations
 *
 * This is fired when the reservations are first loaded, and when a column sort
 * icon is clicked.
 *
 * If a sort icon was clicked, the switch logic is used for the sort field
 * that was clicked. If it's the initial load, sortField and
 * sortDirection will not be passed as params, and the default case will be
 * used in the switch.
 */
function sortReservations(
  reservations: GetReservationsResult[],
  reservationType: WaitlistManagementType,
  sortField?: SortType,
  sortDirection?: SortDirection
): any[] {
  resType = reservationType;
  let dateSortDirection = SortDirection.DESC;
  if (resType === WaitlistManagementType.Active)
    dateSortDirection = SortDirection.ASC

  let sortFunction:any
  switch (sortField) {

    // Sort by waitlist id
    case SortType.WAITLISTID:
      sortFunction = (a:any, b:any) => waitListIdSortFunc(a,b, sortDirection ? sortDirection : SortDirection.ASC);
      break;

    // Sort by Transaction Date
    case SortType.TRANSACTION_DATE:
      sortFunction = (a:any, b:any) => transactionDateSortFunc(a, b, sortDirection ? sortDirection : SortDirection.ASC);
      break;

    // default sort
    default:
      sortFunction = (a:any, b:any) => transactionDateSortFunc(a, b, sortDirection ? sortDirection : SortDirection.ASC);
  }

  // Return the sorted contracts
  return [...reservations].sort(sortFunction);
}

export {
  sortReservations,
  SortType,
  SortDirection,
};